import { useTheme } from '@emotion/react';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  useMediaQuery,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { userSecuritiesAccountsSlice } from '!core/redux';
import { securityAccountSelectedSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { tradingSlice } from '!views/index';
import { TrlFeedback } from '»components/atoms';
import {
  ButtonClose,
  ButtonMain,
  ButtonPencil,
} from '»components/atoms/Buttons';
import { trlInsertOrderServices } from '»components/templates/TrlInsertOrder';
import {
  useCurrentPage,
  useHandleSagaError,
  useNegModalContext,
} from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import {
  BoxCancelMessageStyled,
  BoxTitleMessageStyled,
  DialogStyled,
  FontAwesomeIconStyled,
  IconStyledBox,
} from './TrlManageOrderButton.styles';

export const TrlManageOrderButton = ({
  variant,
  dataItem,
  disabled,
  isCard,
  hide,
  isTextButton,
}) => {
  const theme = useTheme();
  const isAbove700 = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const { isTradingPrimaryPage } = useCurrentPage();

  const { openInsertOrderModal } = useNegModalContext();

  const manageButtons = {
    edit: ButtonPencil,
    cancel: ButtonClose,
  };

  const ManageButtonComponent = manageButtons[variant];

  const buttons = {
    cancel: {
      longText: 'TRLT_UI-CANCEL_ORDER',
      shortText: 'TRLT_UI-CANCEL_ORDER_SHORT',
      id: 'cancelOrderLargeButton',
      onClick: (clickEvent) => {
        clickEvent.stopPropagation();
        setOpen(true);
      },
    },
    edit: {
      longText: 'TRLT_UI-EDIT_ORDER',
      shortText: 'TRLT_UI-EDIT_ORDER_SHORT',
      id: 'editOrderLargeButton',
      onClick: (clickEvent) => {
        if (isMobile) clickEvent.stopPropagation();
        if (isTradingPrimaryPage) {
          navigate('/Insert/Order/Primary', {
            state: { dataItem, edit: true },
          });
        } else {
          if (isMobile)
            dispatch(
              tradingSlice.actions.selectTrade({
                ordBookKey: dataItem.ordBookKey,
                bookType: 'B',
                market: 'stockExchange',
              }),
            );

          openInsertOrderModal();
        }
      },
    },
  };

  const currentBtnAttributes = {
    text:
      isTradingPrimaryPage || isAbove700
        ? buttons[variant].longText
        : buttons[variant].shortText,
    id: buttons[variant].id,
    onClick: buttons[variant].onClick,
  };

  const {
    text: buttonText,
    id: buttonId,
    onClick: handleButtonClick,
  } = currentBtnAttributes;

  const handleClickCloseDialog = () => {
    setOpen(false);
  };

  const buttonWidth = isAbove700 ? '486px' : '219px';

  return (
    <>
      {isTextButton ? (
        <ButtonMain
          disabled={disabled}
          onClick={handleButtonClick}
          variant="secondary"
          expressionId={buttonText}
          width={buttonWidth}
          id={buttonId}
        />
      ) : (
        <ManageButtonComponent
          isCard={isCard}
          onClick={handleButtonClick}
          disabled={disabled}
          hide={hide}
        />
      )}
      {variant === 'cancel' && (
        <CancelOrderDialog
          open={open}
          onClose={handleClickCloseDialog}
          dataItem={dataItem}
        />
      )}
    </>
  );
};

TrlManageOrderButton.propTypes = {
  variant: PropTypes.oneOf(['edit', 'cancel']).isRequired,
  dataItem: PropTypes.object,
  disabled: PropTypes.bool,
  isCard: PropTypes.bool,
  hide: PropTypes.bool,
  isTextButton: PropTypes.bool,
};

const CancelOrderDialog = (props) => {
  const { onClose, open, dataItem } = props;

  const dispatch = useDispatch();

  const securityAccount = useSelector(securityAccountSelectedSelector);
  const [error, setError] = useState(null);

  const { isFundsOrders, isMarketStockExchangeOrders } = useCurrentPage();
  const { handleSagaError } = useHandleSagaError();

  const handleConfirm = (e) => {
    e.stopPropagation();

    trlInsertOrderServices
      .cancelOrder({
        clientId: securityAccount,
        ordBookKey: dataItem.ordBookKey,
      })
      .then((resp) => {
        const [{ text }] = resp;
        setError(text);

        if (!text) {
          dispatch(
            userSecuritiesAccountsSlice.actions.userSecuritiesAccountsRequest(),
          );

          if (isFundsOrders) {
            dispatch(
              tradingSlice.actions.tradingFundsDailyRequest({
                getEvents: false,
              }),
            );
          } else if (isMarketStockExchangeOrders) {
            dispatch(
              tradingSlice.actions.tradingDailyStockExchangeRequest({
                getEvents: false,
              }),
            );
          } else {
            dispatch(tradingSlice.actions.tradingDailyRequest());
          }
          onClose();
        }
      })
      .catch((ex) => {
        handleSagaError(ex);
        setError(ex);
      });
  };
  const handleClose = (e) => {
    e.stopPropagation();

    setError(null);
    onClose();
  };

  return (
    <DialogStyled onClose={handleClose} open={open} maxWidth="lg">
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <IconStyledBox>
            <FontAwesomeIconStyled icon={faCircleExclamation} />
          </IconStyledBox>
          <BoxTitleMessageStyled>
            {TrlFormattedMessagePlain({ id: 'TRLT_UI-CANCEL_ORDER' })}
          </BoxTitleMessageStyled>
          <BoxCancelMessageStyled>
            <FormattedMessage
              id="TRLT_UI-WANT_CANCEL_ORDER"
              values={{ ordBookKey: dataItem?.ordBookKey }}
            />
          </BoxCancelMessageStyled>
          <Divider />
        </DialogContentText>
        {error && (
          <>
            <Divider />
            <TrlFeedback msg={error} />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <ButtonMain
          onClick={handleClose}
          variant="secondary"
          expressionId="TRLT_UI-NO"
        />
        <ButtonMain
          onClick={handleConfirm}
          expressionId="TRLT_UI-YES"
          autoFocus
        />
      </DialogActions>
    </DialogStyled>
  );
};

CancelOrderDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  dataItem: PropTypes.object,
};
